<template>
	<el-dialog
		title="批量导入"
		:visible.sync="dialogVisible"
		width="500px"
		top="15vh"
		:close-on-click-modal="false"
		:before-close="handleClose"
		custom-class="asset-import-dialog">
		<!-- 上传区 -->
		<div class="upload-area">
			<el-upload
				ref="upload"
				class="import-uploader"
				drag
				action="#"
				:auto-upload="false"
				:show-file-list="true"
				:file-list="fileList"
				:limit="1"
				:on-change="handleFileChange"
				:on-remove="handleFileRemove"
				:on-exceed="handleExceed"
				accept=".xlsx,.xls">
				<i class="el-icon-upload"></i>
				<div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
				<div slot="tip" class="el-upload__tip">仅支持 .xlsx / .xls 格式，单次最多导入 5000 条</div>
			</el-upload>
		</div>

		<!-- 上传中提示 -->
		<div v-if="submitting" class="loading-tip">
			<i class="el-icon-loading"></i>
			正在导入中，请稍候...
		</div>

		<!-- 导入结果 -->
		<div v-if="result && !submitting" class="result-area">
			<el-divider content-position="left">导入结果</el-divider>
			<div class="result-stats">
				<div class="stat-item">
					<div class="stat-value total">{{ result.totalCount || 0 }}</div>
					<div class="stat-label">总数</div>
				</div>
				<div class="stat-item success">
					<div class="stat-value">{{ result.successCount || 0 }}</div>
					<div class="stat-label">成功</div>
				</div>
				<div class="stat-item fail">
					<div class="stat-value">{{ result.failCount || 0 }}</div>
					<div class="stat-label">失败</div>
				</div>
			</div>
			<div v-if="failReasons.length" class="fail-reasons">
				<el-collapse>
					<el-collapse-item title="失败原因详情" :name="1">
						<el-table :data="failReasons" size="mini" border>
							<el-table-column type="index" label="序号" width="60"></el-table-column>
							<el-table-column prop="rowNum" label="行号" width="80"></el-table-column>
							<el-table-column prop="reason" label="失败原因"></el-table-column>
						</el-table>
					</el-collapse-item>
				</el-collapse>
			</div>
		</div>

		<!-- 按钮 -->
		<span slot="footer">
			<el-button
				v-if="downloadTemplateFn"
				type="text"
				:loading="downloadLoading"
				@click="handleDownloadTemplate">
				<i class="el-icon-download"></i>下载导入模板
			</el-button>
			<el-button @click="handleClose">关 闭</el-button>
			<el-button
				type="primary"
				:loading="submitting"
				:disabled="!fileList.length || submitting"
				@click="handleSubmit">确 定 导 入</el-button>
		</span>
	</el-dialog>
</template>

<script>
	export default {
		name: 'AssetImportDialog',
		props: {
			dialogVisible: Boolean,
			// 外部传入的导入方法：接收 file，返回 Promise
			importFn: {
				type: Function,
				required: true,
			},
			// 外部传入的模板下载方法，返回 Promise<axiosResponse>（可选）
			downloadTemplateFn: {
				type: Function,
				default: null,
			},
		},
		watch: {
			dialogVisible(val) {
				if (val) {
					this.submitting = false;
					this.result = null;
					this.fileList = [];
					this.$nextTick(() => {
						this.$refs.upload && this.$refs.upload.clearFiles();
					});
				}
			},
		},
		data() {
			return {
				submitting: false,
				downloadLoading: false,
				fileList: [],
				result: null,
			};
		},
		computed: {
			failReasons() {
				const failReasons = (this.result && this.result.failReasons) || [];
				if (!failReasons.length) return [];
				// 兼容后端返回 { rowNum, reason } 或 纯字符串
				return failReasons.map(item => {
					if (typeof item === 'string') {
						return { rowNum: '-', reason: item };
					}
					return item;
				});
			},
		},
		methods: {
			handleDownloadTemplate() {
				if (!this.downloadTemplateFn || this.downloadLoading) return;
				this.downloadLoading = true;
				this.downloadTemplateFn()
					.then(response => {
						const blob = new Blob([response.data]);
						let fileName = '资产导入模板.xlsx';
						const disposition = response.headers && response.headers['content-disposition'];
						if (disposition) {
							const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
							const normalMatch = disposition.match(/filename="?([^";]+)"?/i);
							const raw = utfMatch ? utfMatch[1] : (normalMatch ? normalMatch[1] : '');
							if (raw) {
								fileName = decodeURIComponent(raw);
							}
						}
						const url = window.URL.createObjectURL(blob);
						const link = document.createElement('a');
						link.style.display = 'none';
						link.href = url;
						link.setAttribute('download', fileName);
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
						window.URL.revokeObjectURL(url);
						this.$message.success('下载成功');
					})
					.catch(err => {
						console.log(err);
						this.$message.error('下载模板失败');
					})
					.finally(() => {
						this.downloadLoading = false;
					});
			},
			handleFileChange(file, fileList) {
				this.fileList = fileList.slice(-1); // 只保留最后一个
				this.result = null;
			},
			handleFileRemove() {
				this.fileList = [];
				this.result = null;
			},
			handleExceed() {
				this.$message.warning('只能上传一个文件');
			},
			handleSubmit() {
				if (!this.fileList.length) return;
				this.submitting = true;
				const rawFile = this.fileList[0].raw;
				this.importFn(rawFile)
					.then(res => {
						this.result = res;
						const failCount = res.failCount || 0;
						const successCount = res.successCount || 0;
						if (failCount > 0) {
							this.$message.warning(`导入完成，成功 ${successCount} 条，失败 ${failCount} 条`);
						} else {
							this.$message.success(`导入成功，共 ${successCount} 条`);
						}
						this.$emit('updateSuccess');
					})
					.catch(err => {
						console.log(err);
						this.$message.error(err.message || '导入失败');
					})
					.finally(() => {
						this.submitting = false;
					});
			},
			handleClose() {
				this.$emit('update:dialogVisible', false);
			},
		},
	};
</script>

<style scoped lang="scss">
	.upload-area {
		padding: 10px 0 0;
	}
	.import-uploader ::v-deep .el-upload-dragger {
		padding: 25px;
	}
	.loading-tip {
		text-align: center;
		color: #409eff;
		padding: 10px 0;
		font-size: 14px;
		i {
			margin-right: 6px;
		}
	}
	.result-area {
		margin-top: 10px;
	}
	.result-stats {
		display: flex;
		justify-content: space-around;
		margin-bottom: 10px;
	}
	.stat-item {
		text-align: center;
		.stat-value {
			font-size: 22px;
			font-weight: bold;
			color: #303133;
		}
		.stat-value.total {
			color: #409eff;
		}
		.stat-value.success {
			color: #67c23a;
		}
		.stat-value.fail {
			color: #f56c6c;
		}
		.stat-label {
			font-size: 12px;
			color: #909399;
			margin-top: 2px;
		}
	}
	.fail-reasons {
		max-height: 180px;
		overflow-y: auto;
	}
</style>
